/**
 * Payment guidance generator twin (design-patient-payment-guidance §4.4, §4.13.5, D17).
 *
 * A pure transliteration of the server's `payment_guidance_service.generate`, consuming the wire
 * policy in cents. Both implementations are held to `server-app/tests/fixtures/payment_guidance_fixtures.json`.
 * Every emitted block carries both `text` and `items` (the unused one `null`) so it deep-equals the
 * server's `model_dump()` shape. No form logic lives here — see `utils/feePolicy.ts` (D24).
 *
 * @module utils/paymentGuidance
 */

import type { GuidanceBlock, GuidanceSource, PaymentGuidance, PaymentPolicy } from '@/types/paymentGuidance';

const HEADING = 'About fees';
const NO_FEE = 'Your appointment fee is set by the practice';
const RECEIPT = 'Your dietitian will give you a receipt so you can claim back your rebate:';
const MEDICARE = 'With a GP Chronic Condition Management Plan, Medicare may refund part or all of the cost.';
const PRIVATE_HEALTH = 'With private health insurance, the rebate depends on your level of cover.';
const HICAPS_UNINSURED = 'Without private health insurance, you pay the full fee on the day and claim any Medicare rebate afterwards.';
const BULK_LEAD =
  'Your appointment is bulk billed, so there is nothing to pay on the day if you have a valid ' +
  'GP Chronic Condition Management Plan. Please bring your Medicare card.';
const BILL_IN_FULL_TAIL =
  ', payable on the day. Please bring a credit or debit card, plus your Medicare card or ' +
  'private health insurance card if you have one.';
const HICAPS_TAIL =
  '. Your private health rebate is claimed on the spot with HICAPS, so please bring your health ' +
  'fund card and a credit or debit card for the remaining gap.';
const OTHER_TAIL = '. Your dietitian will explain how payment works when you book.';

/** The platform default is a policy, not a paragraph (D12). Mirrors the server's PLATFORM_DEFAULT_POLICY. */
export const PLATFORM_DEFAULT_POLICY: PaymentPolicy = {
  fee_initial_cents: 12000,
  fee_followup_cents: null,
  billing_method: 'bill_in_full',
  accepts_medicare_ccm: true,
  cancellation_notice_hours: 24,
  cancellation_fee_cents: null,
  payment_notes: null,
};

/** 12000 → "$120 AUD"; 12050 → "$120.50 AUD". Integer arithmetic only (D11). */
export const formatMoney = (cents: number) =>
  `$${Math.floor(cents / 100)}${cents % 100 ? '.' + String(cents % 100).padStart(2, '0') : ''} AUD`;

function feePhrase(policy: PaymentPolicy): string | null {
  const initial = policy.fee_initial_cents !== null ? formatMoney(policy.fee_initial_cents) : null;
  const follow = policy.fee_followup_cents !== null ? formatMoney(policy.fee_followup_cents) : null;
  if (initial && follow) return `Your initial appointment is ${initial} and follow-up appointments are ${follow}`;
  if (initial) return `The full cost of your appointment is ${initial}`;
  if (follow) return `Follow-up appointments are ${follow}`;
  return null;
}

const paragraph = (text: string): GuidanceBlock => ({ kind: 'paragraph', text, items: null });
const bullets = (items: string[]): GuidanceBlock => ({ kind: 'bullets', text: null, items });

/**
 * Deterministic, no I/O. The caller always names the tier (`'default'` for the platform policy,
 * `'dietitian'` for a dietitian's own); the referrer tier is server-only.
 */
export function generatePaymentGuidance(policy: PaymentPolicy, source: Exclude<GuidanceSource, 'referrer'>): PaymentGuidance {
  const fee = feePhrase(policy);
  const method = policy.billing_method;
  let lead: string;
  if (method === 'bulk_bill') {
    // Fee phrase's first character lowercased mid-sentence (server §4.4 rule)
    lead = BULK_LEAD + (fee ? ` Without a plan, ${fee[0].toLowerCase()}${fee.slice(1)}, payable on the day.` : '');
  } else if (method === 'hicaps') {
    lead = `${fee ?? NO_FEE}${HICAPS_TAIL}`;
  } else if (method === 'bill_in_full') {
    lead = `${fee ?? NO_FEE}${BILL_IN_FULL_TAIL}`;
  } else {
    lead = `${fee ?? NO_FEE}${fee ? ', payable on the day' : ''}${OTHER_TAIL}`;
  }

  const body: GuidanceBlock[] = [];
  if (method !== 'bulk_bill') {
    const items = [...(policy.accepts_medicare_ccm ? [MEDICARE] : []), method === 'hicaps' ? HICAPS_UNINSURED : PRIVATE_HEALTH];
    body.push(paragraph(RECEIPT));
    body.push(bullets(items));
  }
  if (policy.payment_notes) {
    body.push(paragraph(policy.payment_notes));
  }

  const hours = policy.cancellation_notice_hours !== null ? policy.cancellation_notice_hours : 24;
  let footnote = `If you need to cancel or reschedule, please give at least ${hours} hours notice.`;
  if (policy.cancellation_fee_cents !== null) {
    footnote += ` Appointments cancelled late or missed may be charged a ${formatMoney(policy.cancellation_fee_cents)} fee by the practice.`;
  }
  return { heading: HEADING, lead, body, footnote, source };
}
